/**
 * Accounting Services › Financial Statements: the statements with comparisons (shared with CFO &
 * Advisory), the trial balance, the general ledger by account, the P&L by class or location, and
 * the statements already published to the client after each close.
 */
import { formatCents } from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, FileText, XCircle } from "lucide-react";
import { useState } from "react";
import { AccountSelect, type Account } from "@/components/bookkeeping";
import { FinancialStatements } from "@/components/financial-statements";
import { Empty, Loading } from "@/components/page";
import { SubTabs } from "@/components/payables";
import { errorMessage } from "@/lib/api";
import { formatDate, formatDateTime, formatPeriod } from "@/lib/format";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";
type Tab = "statements" | "tb" | "gl" | "class" | "location" | "published";

function recentMonths(n: number) {
  const d = new Date();
  return Array.from({ length: n }, (_, i) => new Date(d.getFullYear(), d.getMonth() - 1 - i, 15).toLocaleDateString("en-CA").slice(0, 7));
}

function MonthSelect({ value, onChange, label, months }: { value: string; onChange: (v: string) => void; label: string; months: string[] }) {
  return (
    <label className="text-xs text-muted-foreground">
      <span className="mb-1 block">{label}</span>
      <select aria-label={label} className={field} value={value} onChange={(e) => onChange(e.target.value)}>
        {months.map((m) => (
          <option key={m} value={m}>
            {formatPeriod(m)}
          </option>
        ))}
      </select>
    </label>
  );
}

function BasisSelect({ value, onChange }: { value: string; onChange: (v: "Accrual" | "Cash") => void }) {
  return (
    <label className="text-xs text-muted-foreground">
      <span className="mb-1 block">Basis</span>
      <select aria-label="Basis" className={field} value={value} onChange={(e) => onChange(e.target.value as "Accrual" | "Cash")}>
        <option value="Accrual">Accrual</option>
        <option value="Cash">Cash</option>
      </select>
    </label>
  );
}

export function BooksStatements({ clientId }: { clientId: string }) {
  const [tab, setTab] = useState<Tab>("statements");
  const [glAccount, setGlAccount] = useState("");
  return (
    <div>
      <SubTabs
        value={tab}
        onChange={setTab}
        tabs={[
          ["statements", "Statements"],
          ["tb", "Trial balance"],
          ["gl", "General ledger"],
          ["class", "By class"],
          ["location", "By location"],
          ["published", "Published"],
        ]}
      />
      {tab === "statements" && <FinancialStatements clientId={clientId} scope="books" />}
      {tab === "tb" &&
        <TrialBalance
          clientId={clientId}
          onAccount={(id) => {
            setGlAccount(id);
            setTab("gl");
          }}
        />}
      {tab === "gl" && <GeneralLedger clientId={clientId} account={glAccount} setAccount={setGlAccount} />}
      {(tab === "class" || tab === "location") && <Segments clientId={clientId} by={tab} />}
      {tab === "published" && <Published clientId={clientId} />}
    </div>
  );
}

type Tb = { end: string; start: string; asOf: string; rows: Array<{ accountId: string | null; account: string; debit: number; credit: number }>; debits: number; credits: number; balanced: boolean };

function TrialBalance({ clientId, onAccount }: { clientId: string; onAccount: (id: string) => void }) {
  const months = recentMonths(13);
  const [end, setEnd] = useState(months[0]!);
  const [basis, setBasis] = useState<"Accrual" | "Cash">("Accrual");
  const { data, isLoading, error } = useQuery<Tb>({ queryKey: [`/api/clients/${clientId}/books/trial-balance?end=${end}&basis=${basis}`], retry: false });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <MonthSelect label="As of the end of" value={end} onChange={setEnd} months={months} />
        <BasisSelect value={basis} onChange={setBasis} />
        {data && (
          <span className={cn("ml-auto flex items-center gap-1.5 text-sm font-medium", data.balanced ? "text-emerald-700 dark:text-emerald-300" : "text-destructive")}>
            {data.balanced ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {data.balanced ? "Debits equal credits" : `Out of balance by ${formatCents(Math.abs(data.debits - data.credits))}`}
          </span>
        )}
      </div>
      {isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : (
        <>
          <TableScroll label="Trial balance table" className="rounded-lg border border-border">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-secondary/60 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Account</th>
                  <th className="px-3 py-2 text-right font-medium">Debit</th>
                  <th className="px-3 py-2 text-right font-medium">Credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map((r, i) => (
                  <tr key={`${r.accountId}-${i}`}>
                    <td className="px-2 py-1.5 sm:px-3">
                      {r.accountId ? (
                        <button type="button" className="text-left hover:text-primary hover:underline" onClick={() => onAccount(r.accountId!)}>
                          {r.account}
                        </button>
                      ) : (
                        r.account
                      )}
                    </td>
                    <td className="tabular px-2 py-1.5 text-right sm:px-3">{r.debit ? formatCents(r.debit) : ""}</td>
                    <td className="tabular px-2 py-1.5 text-right sm:px-3">{r.credit ? formatCents(r.credit) : ""}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className="px-3 py-2">Total</td>
                  <td className="tabular px-3 py-2 text-right">{formatCents(data.debits)}</td>
                  <td className="tabular px-3 py-2 text-right">{formatCents(data.credits)}</td>
                </tr>
              </tfoot>
            </table>
          </TableScroll>
          <p className="text-xs text-muted-foreground">
            Balance sheet accounts at {formatDate(periodEndOf(data.end))}; income and expenses from {formatDate(data.start)} (the start of the fiscal year). From QuickBooks as of {formatDateTime(data.asOf)}. Select an account for its general ledger.
          </p>
        </>
      )}
    </div>
  );
}

const periodEndOf = (m: string) => new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0)).toISOString().slice(0, 10);

type Gl = { name: string | null; beginning: number | null; rows: Array<{ date: string; type: string; num: string; name: string; memo: string; split: string; amount: number }>; rowCount: number; truncated: boolean; total: number; asOf: string };

function GeneralLedger({ clientId, account, setAccount }: { clientId: string; account: string; setAccount: (v: string) => void }) {
  const months = recentMonths(24);
  const [from, setFrom] = useState(months[0]!);
  const [to, setTo] = useState(months[0]!);
  const { data: qbo } = useQuery<{ accounts?: Account[] }>({ queryKey: ["/api/clients/", clientId, "/qbo"] });
  const accounts = qbo?.accounts ?? [];
  const ok = !!account && from <= to;
  const { data, isLoading, error } = useQuery<Gl>({ queryKey: [`/api/clients/${clientId}/books/statements/ledger?${new URLSearchParams({ account, from, to, basis: "Accrual" })}`], enabled: ok, retry: false });
  let running = data?.beginning ?? 0;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[16rem] flex-1 text-xs text-muted-foreground">
          <span className="mb-1 block">Account</span>
          <AccountSelect accounts={accounts} value={account} onChange={setAccount} label="Account" />
        </label>
        <MonthSelect label="From" value={from} onChange={setFrom} months={months} />
        <MonthSelect label="To" value={to} onChange={setTo} months={months} />
      </div>
      {!account ? (
        <Empty>Choose an account to see its transactions.</Empty>
      ) : from > to ? (
        <Empty>The start month is after the end month.</Empty>
      ) : isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : (
        <>
          <TableScroll label="General ledger table" className="rounded-lg border border-border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-secondary/60 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Type and number</th>
                  <th className="px-3 py-2 font-medium">Name and memo</th>
                  <th className="px-3 py-2 font-medium">Split</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  {data.beginning !== null && <th className="px-3 py-2 text-right font-medium">Balance</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.beginning !== null && (
                  <tr className="text-muted-foreground">
                    <td className="px-3 py-1.5" colSpan={4}>
                      Beginning balance
                    </td>
                    <td />
                    <td className="tabular px-3 py-1.5 text-right">{formatCents(data.beginning)}</td>
                  </tr>
                )}
                {data.rows.map((r, i) => {
                  running += r.amount;
                  return (
                    <tr key={i} className="align-top">
                      <td className="whitespace-nowrap px-3 py-1.5">{formatDate(r.date)}</td>
                      <td className="px-3 py-1.5">
                        {r.type}
                        {r.num && <span className="block text-xs text-muted-foreground">#{r.num}</span>}
                      </td>
                      <td className="max-w-[16rem] px-3 py-1.5">
                        <span className="block truncate">{r.name || "—"}</span>
                        {r.memo && <span className="block truncate text-xs text-muted-foreground">{r.memo}</span>}
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-1.5">{r.split}</td>
                      <td className={cn("tabular whitespace-nowrap px-3 py-1.5 text-right", r.amount < 0 && "text-red-700 dark:text-red-400")}>{formatCents(r.amount)}</td>
                      {data.beginning !== null && <td className="tabular whitespace-nowrap px-3 py-1.5 text-right">{formatCents(running)}</td>}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className="px-3 py-2" colSpan={4}>
                    Total for {data.name ?? "the account"}
                  </td>
                  <td className="tabular px-3 py-2 text-right">{formatCents(data.total)}</td>
                  {data.beginning !== null && <td className="tabular px-3 py-2 text-right">{formatCents(data.beginning + data.total)}</td>}
                </tr>
              </tfoot>
            </table>
          </TableScroll>
          <p className="text-xs text-muted-foreground">
            {data.rowCount.toLocaleString()} transaction{data.rowCount === 1 ? "" : "s"} from QuickBooks as of {formatDateTime(data.asOf)}.{data.truncated && " Showing the first 2,000; choose fewer months to see the rest."}
          </p>
        </>
      )}
    </div>
  );
}

type Seg = { columns: string[]; lines: Array<{ label: string; kind: "data" | "header" | "total"; depth: number; amounts: Record<string, number> }>; asOf: string };

function Segments({ clientId, by }: { clientId: string; by: "class" | "location" }) {
  const months = recentMonths(13);
  const [from, setFrom] = useState(months[0]!);
  const [to, setTo] = useState(months[0]!);
  const ok = from <= to;
  const { data, isLoading, error } = useQuery<Seg>({ queryKey: [`/api/clients/${clientId}/books/statements/segments?by=${by}&from=${from}&to=${to}`], enabled: ok, retry: false });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <MonthSelect label="From" value={from} onChange={setFrom} months={months} />
        <MonthSelect label="To" value={to} onChange={setTo} months={months} />
      </div>
      {!ok ? (
        <Empty>The start month is after the end month.</Empty>
      ) : isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : data.columns.length <= 1 ? (
        <Empty>No {by === "class" ? "classes" : "locations"} are used on this client's transactions in QuickBooks.</Empty>
      ) : (
        <>
          <TableScroll label={`Profit and loss by ${by === "class" ? "class" : "location"} table`} className="rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-xs text-muted-foreground">
                <tr>
                  <th className="sticky left-0 bg-secondary px-3 py-2 text-left font-medium">Profit and Loss</th>
                  {data.columns.map((c) => (
                    <th key={c} className="px-3 py-2 text-right font-medium">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l, i) =>
                  l.kind === "header" ? (
                    <tr key={i}>
                      <td colSpan={data.columns.length + 1} className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {l.label}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className={cn(l.kind === "total" && "border-t border-border font-semibold")}>
                      <td className="sticky left-0 bg-card px-3 py-1.5" style={{ paddingLeft: `${0.75 + Math.max(0, l.depth - 1) * 0.75}rem` }}>
                        {l.label}
                      </td>
                      {data.columns.map((c) => {
                        const v = l.amounts[c] ?? 0;
                        return (
                          <td key={c} className={cn("tabular whitespace-nowrap px-3 py-1.5 text-right", v < 0 && "text-red-700 dark:text-red-400", v === 0 && "text-muted-foreground")}>
                            {v === 0 ? "—" : formatCents(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </TableScroll>
          <p className="text-xs text-muted-foreground">Accrual basis, from QuickBooks as of {formatDateTime(data.asOf)}. Amounts without a {by === "class" ? "class" : "location"} are under “Not Specified”.</p>
        </>
      )}
    </div>
  );
}

function Published({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery<Array<{ id: string; folder: string; filename: string; createdAt: string; visibility: string }>>({ queryKey: [`/api/documents?clientId=${clientId}`] });
  if (isLoading) return <Loading />;
  const docs = (data ?? []).filter((d) => d.folder === "Financial Statements");
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">Statements are published to the client portal from each month-end close, once its period is locked in QuickBooks, so the client only ever sees final figures.</p>
      {!docs.length ? (
        <Empty>No statements have been published yet.</Empty>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <a href={`/api/documents/${d.id}/download`} className="min-w-0 flex-1 truncate hover:underline">
                {d.filename}
              </a>
              <span className="text-xs text-muted-foreground">{d.visibility === "shared" ? "In the client's portal" : "Internal"}</span>
              <span className="text-xs text-muted-foreground">{formatDate(d.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
